import React, { useMemo, useState } from "react";
import { Box, Stack, Typography, Chip, Divider } from "@mui/material";
import { useTheme } from "@mui/material/styles";

/**
 * Bir çalışmanın gün gün profil / sonda üretimini gösteren sütun grafiği.
 *
 * Projeye grafik kütüphanesi eklemeden, doğrudan SVG ile çiziliyor:
 * paket bağımlılığı yok, paket boyutu artmıyor.
 *
 * seri: [{ gun: "2026-08-17", profil: 3, sonda: 2, profilNumune: 9, sondaNumune: 4 }]
 */

const gunEtiketi = (gun) => {
  if (!gun) return "";
  const [, a, g] = gun.split("-");
  return `${g}.${a}`;
};

const tamTarih = (gun) => {
  if (!gun) return "";
  const [y, a, g] = gun.split("-");
  return `${g}.${a}.${y}`;
};

export default function JobDailyChart({ seri = [], onGunSec, seciliGun }) {
  const theme = useTheme();
  const [hoverIdx, setHoverIdx] = useState(-1);

  const enYuksek = useMemo(
    () => Math.max(1, ...seri.map((g) => Math.max(g.profil, g.sonda))),
    [seri],
  );

  if (seri.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" fontStyle="italic">
        Bu çalışma için henüz kayıt girilmemiş.
      </Typography>
    );
  }

  const YUKSEKLIK = 150;
  const SUTUN = 14;
  const ARALIK = 6;
  const GRUP = SUTUN * 2 + ARALIK;
  const BOSLUK = 18;
  const genislik = seri.length * (GRUP + BOSLUK) + BOSLUK;

  const profilRenk = theme.palette.success.main;
  const sondaRenk = theme.palette.error.main;

  return (
    <Box>
      <Stack direction="row" spacing={2} sx={{ mb: 1.5 }} alignItems="center">
        <Stack direction="row" spacing={0.75} alignItems="center">
          <Box
            sx={{ width: 10, height: 10, borderRadius: 0.5, bgcolor: profilRenk }}
          />
          <Typography variant="caption" fontWeight={700}>
            Profil
          </Typography>
        </Stack>
        <Stack direction="row" spacing={0.75} alignItems="center">
          <Box
            sx={{ width: 10, height: 10, borderRadius: 0.5, bgcolor: sondaRenk }}
          />
          <Typography variant="caption" fontWeight={700}>
            Sonda
          </Typography>
        </Stack>
        <Divider orientation="vertical" flexItem />
        <Typography variant="caption" color="text.secondary">
          Bir güne tıklayarak o günün detayını açabilirsiniz.
        </Typography>
      </Stack>

      <Box sx={{ overflowX: "auto", pb: 1 }}>
        <svg
          width={genislik}
          height={YUKSEKLIK + 46}
          role="img"
          aria-label="Gün gün üretim grafiği"
        >
          {/* yatay kilavuz cizgileri */}
          {[0, 0.25, 0.5, 0.75, 1].map((oran) => {
            const y = YUKSEKLIK - YUKSEKLIK * oran + 8;
            return (
              <g key={oran}>
                <line
                  x1={0}
                  x2={genislik}
                  y1={y}
                  y2={y}
                  stroke="#eef0f2"
                  strokeWidth={1}
                />
                <text x={2} y={y - 3} fontSize="9" fill="#98a2b3">
                  {Math.round(enYuksek * oran)}
                </text>
              </g>
            );
          })}

          {seri.map((g, i) => {
            const x = BOSLUK + i * (GRUP + BOSLUK);
            const pY = (g.profil / enYuksek) * YUKSEKLIK;
            const sY = (g.sonda / enYuksek) * YUKSEKLIK;
            const aktif = seciliGun === g.gun || hoverIdx === i;

            return (
              <g
                key={g.gun}
                onMouseEnter={() => setHoverIdx(i)}
                onMouseLeave={() => setHoverIdx(-1)}
                onClick={() => onGunSec && onGunSec(g.gun)}
                style={{ cursor: onGunSec ? "pointer" : "default" }}
              >
                {aktif && (
                  <rect
                    x={x - 6}
                    y={0}
                    width={GRUP + 12}
                    height={YUKSEKLIK + 32}
                    fill="#f4f6f8"
                    rx={6}
                  />
                )}

                <rect
                  x={x}
                  y={YUKSEKLIK - pY + 8}
                  width={SUTUN}
                  height={pY}
                  fill={profilRenk}
                  rx={3}
                />
                <rect
                  x={x + SUTUN + ARALIK}
                  y={YUKSEKLIK - sY + 8}
                  width={SUTUN}
                  height={sY}
                  fill={sondaRenk}
                  rx={3}
                />

                {g.profil > 0 && (
                  <text
                    x={x + SUTUN / 2}
                    y={YUKSEKLIK - pY + 4}
                    fontSize="9"
                    fontWeight="700"
                    textAnchor="middle"
                    fill={profilRenk}
                  >
                    {g.profil}
                  </text>
                )}
                {g.sonda > 0 && (
                  <text
                    x={x + SUTUN + ARALIK + SUTUN / 2}
                    y={YUKSEKLIK - sY + 4}
                    fontSize="9"
                    fontWeight="700"
                    textAnchor="middle"
                    fill={sondaRenk}
                  >
                    {g.sonda}
                  </text>
                )}

                <text
                  x={x + GRUP / 2}
                  y={YUKSEKLIK + 24}
                  fontSize="10"
                  textAnchor="middle"
                  fill={aktif ? "#101828" : "#667085"}
                  fontWeight={aktif ? 700 : 500}
                >
                  {gunEtiketi(g.gun)}
                </text>
              </g>
            );
          })}
        </svg>
      </Box>

      {hoverIdx > -1 && (
        <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap">
          <Chip
            size="small"
            label={tamTarih(seri[hoverIdx].gun)}
            sx={{ fontWeight: 700 }}
          />
          <Chip
            size="small"
            color="success"
            label={`${seri[hoverIdx].profil} profil / ${seri[hoverIdx].profilNumune} numune`}
          />
          <Chip
            size="small"
            color="error"
            label={`${seri[hoverIdx].sonda} sonda / ${seri[hoverIdx].sondaNumune} katman`}
          />
        </Stack>
      )}
    </Box>
  );
}
